import { zodResolver } from '@hookform/resolvers/zod';
import { can, GROUP_TYPES, groupCreateInput, type MeDto } from '@hudhuria/shared';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute, redirect } from '@tanstack/react-router';
import { useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { z } from 'zod';
import { useGroupMutation } from '~/api/mutations';
import { teamQuery, useCenter, useMe, useRoster } from '~/api/queries';
import {
  Avatar,
  Checkbox,
  Chip,
  EmptyState,
  FieldError,
  FieldLabel,
  Overlay,
  OverlayFooter,
  OverlayHeader,
  PrimaryButton,
  SearchField,
  SegmentedControl,
  TextField,
} from '~/components/ui';
import { useBack } from '~/lib/nav';
import { matchesStudent } from '~/lib/search';
import { toast } from '~/lib/toast';
import { uuid } from '~/lib/uuid';
import { useOnline } from '~/offline/online';

export const Route = createFileRoute('/_app/groups/new')({
  validateSearch: z.object({ student: z.string().optional() }),
  beforeLoad: ({ context }) => {
    const me = context.queryClient.getQueryData<MeDto>(['me']);
    if (!can(me?.staff.role, 'groups')) throw redirect({ to: '/hub' });
  },
  component: NewGroup,
});

const form = z.object({
  name: groupCreateInput.shape.name,
  type: z.enum(GROUP_TYPES),
  studentIds: z.array(z.string()).min(1, 'Pick at least one student').max(500),
  teacherIds: z.array(z.string()).max(50),
});
type Form = z.infer<typeof form>;

/** A class, session or event cohort that staff can roll-call in one tap. Works offline — it syncs later. */
function NewGroup() {
  const { student } = Route.useSearch();
  const back = useBack();
  const me = useMe().data;
  const center = useCenter();
  const online = useOnline();
  const roster = useRoster().data;
  const team = useQuery({ ...teamQuery, enabled: online });
  const create = useGroupMutation();
  const [q, setQ] = useState('');

  const { control, register, handleSubmit, watch, formState } = useForm<Form>({
    resolver: zodResolver(form),
    defaultValues: { name: '', type: 'School', studentIds: student ? [student] : [], teacherIds: [] },
  });
  const picked = watch('studentIds');

  // Home-center students by default; a search reaches the whole institution.
  const list = useMemo(() => {
    if (!roster || !center) return [];
    const term = q.trim();
    const base = term
      ? roster.filter((s) => matchesStudent(s, term))
      : roster.filter((s) => s.homeCenterId === center.id);
    return [...base].sort((a, b) => a.name.localeCompare(b.name));
  }, [roster, center, q]);
  const teachers = (team.data ?? []).filter(
    (m) => m.role === 'teacher' && center && m.centerIds.includes(center.id),
  );

  if (!me || !center) return <Overlay>{null}</Overlay>;

  const submit = (v: Form) => {
    create.mutate({ input: { id: uuid(), centerId: center.id, ...v } });
    toast(`${v.name.trim()} created · ${v.studentIds.length} students`);
    back();
  };

  return (
    <Overlay>
      <OverlayHeader
        back="Groups"
        onBack={back}
        title="New group"
        subtitle={`${center.name} · roll-call everyone in one tap`}
        pb={10}
      />
      <form
        id="new-group"
        onSubmit={handleSubmit(submit)}
        noValidate
        className="flex flex-1 flex-col gap-[18px] px-5 pt-1.5 pb-4 scroll-y"
      >
        <TextField
          label="Group name"
          placeholder="e.g. Moi Primary · Grade 6"
          autoComplete="off"
          error={formState.errors.name?.message}
          {...register('name')}
        />
        <div>
          <FieldLabel>Type</FieldLabel>
          <Controller
            control={control}
            name="type"
            render={({ field }) => (
              <SegmentedControl
                value={field.value}
                onChange={field.onChange}
                options={GROUP_TYPES.map((t) => ({ value: t, label: t }))}
              />
            )}
          />
        </div>

        <div>
          <FieldLabel>Teachers</FieldLabel>
          {!online ? (
            <div className="text-[12.5px] text-slate-500">
              Connect to assign teachers — admins can roll-call this group meanwhile.
            </div>
          ) : team.isPending ? (
            <div className="text-[12.5px] text-slate-400">Loading team…</div>
          ) : teachers.length === 0 ? (
            <div className="text-[12.5px] text-slate-500">
              No teachers at {center.name} yet. Invite one from Team & roles.
            </div>
          ) : (
            <Controller
              control={control}
              name="teacherIds"
              render={({ field }) => (
                <div className="flex flex-wrap gap-[7px]">
                  {teachers.map((t) => {
                    const on = field.value.includes(t.id);
                    return (
                      <Chip
                        key={t.id}
                        on={on}
                        onClick={() =>
                          field.onChange(on ? field.value.filter((x) => x !== t.id) : [...field.value, t.id])
                        }
                      >
                        {t.name || t.email}
                      </Chip>
                    );
                  })}
                </div>
              )}
            />
          )}
          <div className="mt-1.5 text-[11.5px] text-slate-400">
            Teachers only see the groups assigned to them.
          </div>
        </div>

        <Controller
          control={control}
          name="studentIds"
          render={({ field }) => {
            const set = new Set(field.value);
            const toggle = (id: string) =>
              field.onChange(set.has(id) ? field.value.filter((x) => x !== id) : [...field.value, id]);
            const allShown = list.length > 0 && list.every((s) => set.has(s.id));
            return (
              <div>
                <div className="mb-2 flex items-baseline justify-between">
                  <div className="text-[12.5px] font-semibold text-slate-600">
                    Students · {field.value.length} picked
                  </div>
                  {list.length > 0 && (
                    <button
                      type="button"
                      onClick={() =>
                        field.onChange(
                          allShown
                            ? field.value.filter((id) => !list.some((s) => s.id === id))
                            : [...new Set([...field.value, ...list.map((s) => s.id)])],
                        )
                      }
                      className="cursor-pointer border-none bg-transparent p-0 text-[12.5px] font-semibold text-brand"
                    >
                      {allShown ? 'Clear these' : `Add all ${list.length}`}
                    </button>
                  )}
                </div>
                <SearchField value={q} onChange={setQ} placeholder="Search name, ref or school" />
                <FieldError>{formState.errors.studentIds?.message}</FieldError>
                <div className="mt-2 flex flex-col overflow-hidden rounded-2xl border border-line">
                  {list.map((s) => (
                    <button
                      type="button"
                      key={s.id}
                      onClick={() => toggle(s.id)}
                      className="flex cursor-pointer items-center gap-3 border-0 border-b border-solid border-line-faint bg-white px-[14px] py-[10px] text-left last:border-b-0"
                    >
                      <Avatar name={s.name} photoUrl={s.photoUrl} size={36} radius="full" fontSize={12} />
                      <div className="min-w-0 flex-1">
                        <div className="truncate text-[14px] font-semibold">{s.name}</div>
                        <div className="truncate text-[11.5px] text-slate-400">
                          {s.ref} · {s.school}
                        </div>
                      </div>
                      <Checkbox on={set.has(s.id)} />
                    </button>
                  ))}
                  {list.length === 0 && (
                    <EmptyState>
                      {q ? 'No student matches that search.' : `No students at ${center.name} yet.`}
                    </EmptyState>
                  )}
                </div>
              </div>
            );
          }}
        />
      </form>
      <OverlayFooter>
        <PrimaryButton type="submit" form="new-group" className="p-[15px] text-[15px]">
          Create group · {picked.length} {picked.length === 1 ? 'student' : 'students'}
        </PrimaryButton>
      </OverlayFooter>
    </Overlay>
  );
}
